import React, { useMemo, useState } from "react";
import styled from "styled-components";

interface Props {
  searchValue: string;
  selectedValue?: string;
  onSearchChange: (newSearchValue: string) => void;
  placeholder?: string;
  isOpen: boolean;
  hasError?: boolean;
  distinctPlaceholder?: boolean;
  leftIcon?: JSX.Element;
}

const AnimatedSelectSearchInput = ({
  searchValue,
  selectedValue,
  onSearchChange,
  placeholder,
  isOpen,
  hasError,
  distinctPlaceholder = false,
  leftIcon,
}: Props) => {
  const [isFocused, setIsFocused] = useState(false);

  const valueToShow = useMemo(() => {
    return isFocused || isOpen ? searchValue : selectedValue;
  }, [isFocused, searchValue, selectedValue, isOpen]);

  const placeholderToShow = useMemo(() => {
    return !!selectedValue ? selectedValue : placeholder;
  }, [selectedValue, placeholder]);

  return (
    <MainContainer>
      {!!leftIcon && (
        <IconContainer dimmed={isFocused || isOpen}>{leftIcon}</IconContainer>
      )}
      <SearchInput
        value={valueToShow}
        placeholder={placeholderToShow}
        onChange={(e) => onSearchChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        hasError={hasError}
        distinctPlaceholder={distinctPlaceholder}
      />
    </MainContainer>
  );
};

const IconContainer = styled.div<{ dimmed: boolean }>`
  width: fit-content;
  height: fit-content;
  ${({ dimmed }) => dimmed && `opacity: 0.6;`}
`;

const MainContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input<{
  hasError?: boolean;
  distinctPlaceholder?: boolean;
}>`
  height: 100%;
  padding: 0;
  border: 0;
  font-size: 14px;
  font-family: ${({ theme }) => theme.redesign.regularFont};
  width: 100%;
  color: ${({ theme, hasError }) =>
    hasError ? theme.redesign.errorColor : theme.redesign.defaultColor};
  outline: 0;

  ::placeholder {
    color: ${({ theme, distinctPlaceholder, hasError }) =>
      hasError
        ? theme.redesign.errorColor
        : distinctPlaceholder
        ? theme.redesign.form.textField.placeholderColor
        : theme.redesign.defaultColor};
  }
`;

export default AnimatedSelectSearchInput;
